import api from './api';
import type { Page, PageResponse, PagesResponse, PageType } from '@/types/page';

export interface PageFilters {
  type?: PageType;
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  search?: string;
}

export interface PageQuery extends PageFilters {
  page?: number;
  limit?: number;
  includeSections?: boolean;
}

class PageService {
  /**
   * Get page by slug (PUBLIC/PRIVATE endpoint depending on auth)
   */
  async getPageBySlug(slug: string, includeSections = true): Promise<Page> {
    try {
      const params = new URLSearchParams();
      if (includeSections) {
        params.append('includeSections', 'true');
      }

      const queryString = params.toString();
      const url = `/pages/slug/${slug}${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<PageResponse>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch page');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching page by slug:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch page'
      );
    }
  }

  /**
   * Get page by ID (PRIVATE endpoint)
   */
  async getPageById(id: string, includeSections = true): Promise<Page> {
    try {
      const params = new URLSearchParams();
      if (includeSections) {
        params.append('includeSections', 'true');
      }

      const queryString = params.toString();
      const url = `/pages/${id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<PageResponse>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch page');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching page by ID:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch page'
      );
    }
  }

  /**
   * Get all pages with filters (PRIVATE endpoint)
   */
  async getPages(query: PageQuery = {}): Promise<{
    pages: Page[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    try {
      const params = new URLSearchParams();
      
      if (query.type) params.append('type', query.type);
      if (query.status) params.append('status', query.status);
      if (query.search) params.append('search', query.search);
      if (query.page) params.append('page', query.page.toString());
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.includeSections) params.append('includeSections', 'true');

      const queryString = params.toString();
      const url = `/pages${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get<PagesResponse>(url);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch pages');
      }

      return {
        pages: response.data.data.data,
        pagination: response.data.data.pagination
      };
    } catch (error) {
      console.error('Error fetching pages:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch pages'
      );
    }
  }

  /**
   * Get home page for current company
   */
  async getHomePage(): Promise<Page> {
    try {
      // First try to get by slug 'home'
      try {
        return await this.getPageBySlug('home', true);
      } catch {
        // Fallback: get pages by type HOME
        const result = await this.getPages({ 
          type: 'HOME', 
          status: 'PUBLISHED',
          includeSections: true,
          limit: 1 
        });
        
        if (result.pages.length === 0) {
          throw new Error('No home page found');
        }
        
        return result.pages[0];
      }
    } catch (error) {
      console.error('Error fetching home page:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch home page'
      );
    }
  }

  /**
   * Create page (PRIVATE endpoint)
   */
  async createPage(data: {
    title: string;
    slug: string;
    type: PageType;
    description?: string;
    seoTitle?: string;
    seoDescription?: string;
    seoKeywords?: string[];
    ogImage?: string;
    config?: Record<string, any>;
  }): Promise<Page> {
    try {
      const response = await api.post<PageResponse>('/pages', data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create page');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error creating page:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to create page'
      );
    }
  }

  /**
   * Update page (PRIVATE endpoint)
   */
  async updatePage(id: string, data: Partial<{
    title: string;
    slug: string;
    type: PageType;
    description: string;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    seoTitle: string;
    seoDescription: string;
    seoKeywords: string[];
    ogImage: string;
    config: Record<string, any>;
  }>): Promise<Page> {
    try {
      const response = await api.put<PageResponse>(`/pages/${id}`, data);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update page');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error updating page:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to update page'
      );
    }
  }

  /**
   * Delete page (PRIVATE endpoint)
   */
  async deletePage(id: string): Promise<void> {
    try {
      const response = await api.delete(`/pages/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete page');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to delete page'
      );
    }
  }

  /**
   * Check if user has access to page management
   */
  hasPageAccess(): boolean {
    try {
      const token = localStorage.getItem('inmocms_auth_token');
      return !!token;
    } catch {
      return false;
    }
  }

  /**
   * Get fallback page data structure
   */
  getFallbackPageData(type: PageType = 'HOME'): Page {
    const now = new Date().toISOString();
    
    return {
      id: 'fallback',
      title: type === 'HOME' ? 'Inicio' : type,
      slug: type.toLowerCase(),
      type,
      description: `Página ${type.toLowerCase()}`,
      status: 'PUBLISHED',
      seoTitle: `Página ${type}`,
      seoDescription: `Descripción de la página ${type}`,
      seoKeywords: [type.toLowerCase()],
      config: {},
      createdAt: now,
      updatedAt: now,
      sections: []
    };
  }
}

export const pageService = new PageService();
export default pageService;